import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, ApiError, setAuthToken } from '../lib/api';
import type { Product, User } from '../lib/types';
import { useCatalog } from './CatalogContext';

const KEYS = { token: 'app.token', user: 'app.user', cart: 'app.cart' };

type CheckoutResult =
  | { status: 'done' }
  | { status: 'pay'; orderId: string; amount: number; currency: string; keyId: string; checkoutUrl: string };

type AppValue = {
  ready: boolean;
  user: User | null;
  isLoggedIn: boolean;
  cart: string[];
  library: string[];
  sendOtp: (email: string, profile?: { name: string; phone: string }) => Promise<{ demoOtp?: string; registered: boolean }>;
  verifyOtp: (email: string, otp: string) => Promise<void>;
  signOut: () => void;
  addToCart: (id: string) => void;
  removeFromCart: (id: string) => void;
  isInCart: (id: string) => boolean;
  checkout: () => Promise<CheckoutResult>;
  confirmPayment: (f: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => Promise<void>;
  ownsProduct: (id: string) => boolean;
  cartProducts: Product[];
  libraryProducts: Product[];
  cartTotal: { price: number; mrp: number };
};

const AppContext = createContext<AppValue | undefined>(undefined);

const read = (k: string) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
};
const write = (k: string, v: unknown) => localStorage.setItem(k, JSON.stringify(v));

export function AppProvider({ children }: { children: React.ReactNode }) {
  const { getProduct, products } = useCatalog();
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [cart, setCart] = useState<string[]>([]);
  const [library, setLibrary] = useState<string[]>([]);

  const loadLibrary = useCallback(async () => {
    try {
      const { productIds } = await api.library();
      setLibrary(productIds);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    (async () => {
      const t = read(KEYS.token);
      const u = read(KEYS.user);
      const c = read(KEYS.cart);
      if (c) setCart(c);
      if (t && u) {
        setAuthToken(t);
        setToken(t);
        setUser(u);
        try {
          const { user: fresh } = await api.me();
          setUser({ name: fresh.name, email: fresh.email, phone: fresh.phone });
          setLibrary(fresh.library ?? []);
          await loadLibrary();
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) {
            setAuthToken(null);
            setToken(null);
            setUser(null);
            localStorage.removeItem(KEYS.token);
            localStorage.removeItem(KEYS.user);
          }
        }
      }
      setReady(true);
    })();
  }, [loadLibrary]);

  // Prune a stale cart: once the catalog has loaded, drop any saved cart ids
  // that no longer exist (e.g. removed/renamed products from an old session).
  useEffect(() => {
    if (products.length === 0) return;
    setCart((prev) => {
      const valid = prev.filter((id) => products.some((p) => p.id === id));
      if (valid.length !== prev.length) write(KEYS.cart, valid);
      return valid.length !== prev.length ? valid : prev;
    });
  }, [products]);

  const sendOtp = useCallback(async (email: string, profile?: { name: string; phone: string }) => {
    const res = await api.sendOtp(email, profile);
    return { demoOtp: res.demoOtp, registered: res.registered };
  }, []);

  const verifyOtp = useCallback(
    async (email: string, otp: string) => {
      const { token: tok, user: u } = await api.verifyOtp(email, otp);
      setAuthToken(tok);
      setToken(tok);
      const profile = { name: u.name, email: u.email, phone: u.phone };
      setUser(profile);
      setLibrary(u.library ?? []);
      write(KEYS.token, tok);
      write(KEYS.user, profile);
      await loadLibrary();
    },
    [loadLibrary]
  );

  const signOut = useCallback(() => {
    setAuthToken(null);
    setToken(null);
    setUser(null);
    setLibrary([]);
    setCart([]);
    localStorage.removeItem(KEYS.token);
    localStorage.removeItem(KEYS.user);
    localStorage.removeItem(KEYS.cart);
  }, []);

  const addToCart = useCallback((id: string) => {
    setCart((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      write(KEYS.cart, next);
      return next;
    });
  }, []);
  const removeFromCart = useCallback((id: string) => {
    setCart((prev) => {
      const next = prev.filter((x) => x !== id);
      write(KEYS.cart, next);
      return next;
    });
  }, []);

  const checkout = useCallback(async (): Promise<CheckoutResult> => {
    // Only send ids that resolve to a real catalog product.
    const validIds = cart.filter((id) => !!getProduct(id));
    if (validIds.length === 0) return { status: 'done' };
    const res = await api.createPayment(validIds);
    if (res.mode === 'razorpay') {
      return { status: 'pay', orderId: res.orderId, amount: res.amount, currency: res.currency, keyId: res.keyId, checkoutUrl: res.checkoutUrl };
    }
    setLibrary(res.library);
    setCart([]);
    write(KEYS.cart, []);
    return { status: 'done' };
  }, [cart, getProduct]);

  const confirmPayment = useCallback(
    async (f: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => {
      const { library: updated } = await api.verifyPayment(f);
      setLibrary(updated);
      setCart([]);
      write(KEYS.cart, []);
    },
    []
  );

  const cartProducts = useMemo(() => cart.map(getProduct).filter(Boolean) as Product[], [cart, getProduct]);
  const libraryProducts = useMemo(() => library.map(getProduct).filter(Boolean) as Product[], [library, getProduct]);
  const cartTotal = useMemo(
    () => cartProducts.reduce((a, p) => ({ price: a.price + p.price, mrp: a.mrp + (p.mrp || p.price) }), { price: 0, mrp: 0 }),
    [cartProducts]
  );

  const value: AppValue = {
    ready,
    user,
    isLoggedIn: !!token,
    cart,
    library,
    sendOtp,
    verifyOtp,
    signOut,
    addToCart,
    removeFromCart,
    isInCart: (id) => cart.includes(id),
    checkout,
    confirmPayment,
    ownsProduct: (id) => library.includes(id),
    cartProducts,
    libraryProducts,
    cartTotal,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
